/**
 * Review & send — the month-end bills, waiting for the owner.
 *
 * Owner, Oct 1 2026, after September's month-end sent wrong bills on its own:
 * "review first". Month-end makes the bills and texts him; here he checks each
 * one (who, how much, how it will go, its lines), opens any to fix it, unticks
 * any that should not go, and taps "Send N bills". Nothing reaches a customer
 * before that (server/services/billReview, invoiceSend.approveAndSend). A bill
 * that could not go stays on the list and is tried again every 30 minutes.
 * Shows only when there is a month with bills waiting (or results to read);
 * otherwise one quiet line. Oct 26 2026 ("the review check can move to Needs
 * Attention"): the nudge is the Needs Attention line (billReview.attentionItem),
 * which lands here as /billing?review=<period_end> with this card open; on its
 * own the card is folded to one line — "12 bills waiting · $1,440.00".
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { ChevronDown } from 'lucide-react';
import { api } from '../services/api';
import FoldCard from './FoldCard';

interface ReviewLine { id: number; job_id: number | null; description: string; amount: number; service_date?: string | null; }
interface ReviewBill {
  id: number; invoice_number: string; customer_id: number; customer_name: string; total: number;
  ways: string[]; why: string | null; autopay: boolean; how: string;
  hold?: string | null;   // waiting on a one-off job's before / after photos (server services/oneOffGates)
  to: { email: string | null; phone: string | null };
  ticked: boolean; lines: ReviewLine[];
  approved: boolean; pending: boolean; error: string | null; tries: number;
}
interface ReviewPeriod { period_start: string; period_end: string; label: string; bills?: number; total?: number; }
interface ReviewData {
  period: ReviewPeriod | null; periods: ReviewPeriod[]; bills: ReviewBill[];
  totals: { bills: number; total: number; ticked: number; ticked_total: number };
  email_on: boolean; sms_on: boolean;
}
interface SendResult {
  id: number; invoice_number: string | null; customer_name: string | null; total: number; status: string | null;
  sent: { channel: string; to: string }[]; paid_by_card: boolean; pending: boolean; will_retry: boolean;
  error: string | null; skipped: string | null; held?: boolean;
}

const money = (n: any) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;
/** "Sep 10" — the day a line's work was done (invoice_line_items.service_date); a month line has none. */
const day = (d?: string | null) => (d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');

function resultWords(r: SendResult): { text: string; tone: 'ok' | 'warn' | 'bad' } {
  if (r.paid_by_card) return { text: 'Paid by the card on file — they got a receipt', tone: 'ok' };
  if (r.sent.length) return { text: `Sent by ${r.sent.map(s => `${s.channel === 'sms' ? 'text' : 'email'} (${s.to})`).join(' and ')}`, tone: 'ok' };
  if (r.pending) return { text: `Not sent: ${r.error || 'unknown'} — ${r.will_retry ? 'will retry every 30 min' : 'stopped retrying; fix it and send again'}`, tone: 'bad' };
  return { text: `Not sent — ${r.skipped || 'nothing to send'}`, tone: 'warn' };
}

/** Pure: the folded line — "12 bills waiting · $1,440.00 — nothing has gone to customers". */
export const foldWords = (count: number, total: number): string =>
  (count ? `${plural(count, 'bill')} waiting · ${money(total)} — nothing has gone to customers yet.` : 'No month-end bills waiting to check.');

export default function ReviewSendCard({ onOpen, wanted }: {
  onOpen: (invoiceId: number) => void;
  /** From /billing?review=<period_end> (Needs Attention): open the card on that month ('' = the latest month). */
  wanted?: string | null;
}) {
  const qc = useQueryClient();
  const [periodEnd, setPeriodEnd] = useState<string | null>(wanted || null);
  const [ticks, setTicks] = useState<Record<number, boolean>>({});
  const [openLines, setOpenLines] = useState<Record<number, boolean>>({});
  const [results, setResults] = useState<SendResult[] | null>(null);
  useEffect(() => { if (wanted) { setPeriodEnd(wanted); setResults(null); } }, [wanted]);

  const { data, isLoading } = useQuery(['billing-review', periodEnd],
    () => api.get('/billing/review', { params: periodEnd ? { period_end: periodEnd } : {} }).then(r => r.data as ReviewData),
    { retry: false });
  const bills = useMemo(() => data?.bills || [], [data]);

  // A bill new to the list starts ticked as the server says (unticked when there is no way to send it,
  // or nothing is owed); one he already ticked or unticked keeps his choice.
  useEffect(() => {
    setTicks(prev => {
      const next: Record<number, boolean> = {};
      for (const b of bills) next[b.id] = b.id in prev ? prev[b.id] : b.ticked;
      return next;
    });
  }, [bills]);

  const chosen = bills.filter(b => ticks[b.id]);
  const chosenTotal = chosen.reduce((t, b) => t + (Number(b.total) || 0), 0);

  // Each bill goes with the total he saw: one that changed since (month-end added to it, someone edited it)
  // is not sent — it comes back on the list for him to check again.
  const send = useMutation((picked: ReviewBill[]) => api.post('/billing/review/send', {
    ids: picked.map(b => b.id), totals: Object.fromEntries(picked.map(b => [b.id, b.total])),
  }).then(r => r.data), {
    onSuccess: (d: any) => {
      const res: SendResult[] = d?.results || [];
      setResults(res);
      const bad = res.some(r => !r.sent.length && !r.paid_by_card);
      (bad ? toast.warn : toast.success)(d?.message || 'Sent', { autoClose: 9000 });
      qc.invalidateQueries('billing-review'); qc.invalidateQueries('invoices'); qc.invalidateQueries('billing-dashboard');
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message || 'Could not send the bills');
      qc.invalidateQueries('billing-review');
    },
  });

  if (isLoading || !data) return null;
  // Nothing to check: one quiet line, no card (the Needs Attention line is what nudges him).
  if (!bills.length && !results) return <p className="text-xs text-gray-500 px-1">Month-end bills: none waiting to check.</p>;

  const nothingCanGo = !data.email_on && !data.sms_on;

  return (
    <FoldCard storageKey="billing_fold_review" tone="brand" forceOpen={wanted != null}
      title={<>Review &amp; send{data.period ? ` — ${data.period.label}` : ''}</>}
      summary={foldWords(bills.length, data.totals.total)}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <p className="text-xs text-gray-600">{bills.length ? 'Untick any that should not go, open one to fix it, then send.' : 'No bills waiting for this month.'}</p>
        {data.periods.length > 1 && (
          <select value={data.period?.period_end || ''} onChange={e => { setPeriodEnd(e.target.value); setResults(null); }}
            className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm bg-white">
            {data.periods.map(p => <option key={p.period_end} value={p.period_end}>{p.label} ({p.bills})</option>)}
          </select>
        )}
      </div>

      {nothingCanGo && bills.length > 0 && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Email and texting are not set up, so no bill can be sent from here yet.
        </p>
      )}

      {bills.length > 0 && (
        <>
          <div className="flex items-center gap-3 text-xs">
            <button type="button" className="text-brand-700 font-medium underline"
              onClick={() => setTicks(Object.fromEntries(bills.map(b => [b.id, !b.hold && (b.ways.length > 0 || b.autopay)])))}>Tick all</button>
            <button type="button" className="text-gray-600 font-medium underline"
              onClick={() => setTicks(Object.fromEntries(bills.map(b => [b.id, false])))}>Untick all</button>
          </div>
          <ul className="divide-y divide-gray-100 border-t border-b border-gray-100">
            {bills.map(b => {
              const noWay = !b.ways.length && !b.autopay;
              const linesOpen = !!openLines[b.id];
              return (
                <li key={b.id} className="py-2.5">
                  <div className="flex items-start gap-3">
                    <input type="checkbox" className="mt-1 h-5 w-5 flex-shrink-0" checked={!!ticks[b.id]}
                      aria-label={`Send ${b.customer_name}'s bill`}
                      onChange={e => setTicks(t => ({ ...t, [b.id]: e.target.checked }))} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-semibold text-gray-900 truncate">{b.customer_name}</p>
                        <p className="text-sm font-bold text-gray-900 flex-shrink-0">{money(b.total)}</p>
                      </div>
                      <p className={`text-xs ${noWay ? 'text-amber-800 font-medium' : 'text-gray-600'}`}>
                        {b.how}{b.to.email ? ` · ${b.to.email}` : ''}{b.to.phone ? ` · ${b.to.phone}` : ''}
                      </p>
                      {b.autopay && (
                        <span className="inline-block mt-0.5 text-[11px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">
                          card on file — charged when you send
                        </span>
                      )}
                      {b.hold && (
                        <p className="text-xs text-amber-800 font-medium mt-0.5">{b.hold}</p>
                      )}
                      {b.approved && b.pending && (
                        <p className="text-xs text-red-700">Approved, not sent yet{b.error ? `: ${b.error}` : ''} — {b.tries >= 5 ? 'stopped retrying' : 'retrying every 30 min'}.</p>
                      )}
                      <div className="flex items-center gap-3 mt-1 text-xs">
                        <span className="text-gray-500">{b.invoice_number}</span>
                        <button type="button" onClick={() => setOpenLines(o => ({ ...o, [b.id]: !o[b.id] }))}
                          className="inline-flex items-center gap-0.5 text-gray-700 font-medium">
                          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${linesOpen ? '' : '-rotate-90'}`} />
                          {plural(b.lines.length, 'line')}
                        </button>
                        <button type="button" onClick={() => onOpen(b.id)} className="text-brand-700 font-medium underline">Open to edit</button>
                      </div>
                      {linesOpen && (
                        <ul className="mt-1.5 space-y-1 rounded-lg bg-gray-50 px-2.5 py-2">
                          {b.lines.map(l => (
                            <li key={l.id} className="flex items-start gap-2 text-xs">
                              <span className="w-11 flex-shrink-0 text-gray-500 tabular-nums">{day(l.service_date)}</span>
                              <span className="text-gray-700 min-w-0 flex-1 break-words">{l.description}</span>
                              <span className={`flex-shrink-0 ${l.amount < 0 ? 'text-emerald-700' : 'text-gray-900'}`}>{money(l.amount)}</span>
                            </li>
                          ))}
                          {!b.lines.length && <li className="text-xs text-gray-500">No lines.</li>}
                        </ul>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
          <button type="button" disabled={!chosen.length || send.isLoading}
            onClick={() => {
              if (window.confirm(`Send ${plural(chosen.length, 'bill')} (${money(chosenTotal)}) to the customers now? Each goes their own way (email or text); a customer on autopay is charged on the card on file and gets a receipt.`)) {
                send.mutate(chosen);
              }
            }}
            className="press w-full sm:w-auto px-4 py-2.5 text-sm font-semibold text-black bg-brand-600 rounded-xl hover:bg-brand-500 disabled:opacity-50">
            {send.isLoading ? 'Sending…' : chosen.length ? `Send ${plural(chosen.length, 'bill')} (${money(chosenTotal)})` : 'Tick the bills to send'}
          </button>
        </>
      )}

      {results && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-bold text-gray-900">What happened</p>
            <button type="button" onClick={() => { setResults(null); if (!bills.length) setPeriodEnd(null); }} className="text-xs text-gray-600 underline">Done</button>
          </div>
          <ul className="space-y-1">
            {results.map(r => {
              const w = resultWords(r);
              return (
                <li key={r.id} className="text-xs">
                  <span className="font-semibold text-gray-900">{r.customer_name || `Bill #${r.id}`}</span>
                  <span className="text-gray-500"> · {r.invoice_number} · {money(r.total)}</span>
                  <span className={`block ${w.tone === 'ok' ? 'text-emerald-700' : w.tone === 'bad' ? 'text-red-700' : 'text-amber-800'}`}>{w.text}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </FoldCard>
  );
}
